import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { registerSW, swUrl } from "@/lib/push";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import HomeworkPage from "@/pages/HomeworkPage";
import TodosPage from "@/pages/TodosPage";
import VaultPage from "@/pages/VaultPage";
import SettingsPage from "@/pages/SettingsPage";
import JarvisPage from "@/pages/JarvisPage";
import GoogleCallback from "@/pages/GoogleCallback";
import WhatsNewPage from "@/pages/WhatsNewPage";
import HealthPage from "@/pages/HealthPage";
import StudyReminderPage from "@/pages/StudyReminderPage";
import ResultsPage from "@/pages/ResultsPage";
import GradesPage from "@/pages/GradesPage";
import GradesPrintPage from "@/pages/GradesPrintPage";
import SubjectCatalogPage from "@/pages/SubjectCatalogPage";
import ClassroomLinksPage from "@/pages/ClassroomLinksPage";
import NotificationsPage from "@/pages/NotificationsPage";
import Laddaren from "@/pages/Laddaren";
import NewsDetailPage from "@/pages/NewsDetailPage";
import BottomNav from "@/components/BottomNav";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import PushPrompt from "@/components/PushPrompt";
import { InstallModal } from "@/components/InstallModal";
import { UpdateBanner } from "@/components/UpdateBanner";

// Lyssnar på NAVIGATE-meddelanden från service workern (klick på pushnotis
// när appen redan är öppen) och gör SPA-navigering utan omladdning.
const SwNavigationListener = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    // Skottsäker deep-link: SW:n sparar sökvägen i Cache Storage vid notisklick.
    // Vi konsumerar den vid start + varje gång appen blir synlig/fokuserad
    // (iOS tappar ofta postMessage till suspenderade PWA-sidor).
    const flushTelemetry = async (extra) => {
      try {
        const c = await caches.open("sw-telemetry-v1");
        const r = await c.match("/__logs__");
        const logs = r ? await r.json() : [];
        if (extra) logs.push({ ...extra, ts: Date.now() });
        if (!logs.length) return;
        await c.delete("/__logs__");
        api.post("/push/diag", { logs }).catch(() => {});
      } catch { /* tyst */ }
    };
    const consumePendingNav = async (trigger) => {
      try {
        const c = await caches.open("pending-nav-v1");
        const r = await c.match("/__pending_nav__");
        if (!r) return;
        await c.delete("/__pending_nav__");
        const { path, ts } = await r.json();
        const fresh = typeof path === "string" && Date.now() - (ts || 0) < 60 * 1000;
        flushTelemetry({ type: "app_pending_nav", trigger, path, fresh });
        if (fresh) navigate(path);
      } catch { /* tyst */ }
    };
    consumePendingNav("mount");
    flushTelemetry(null);
    // Boot-rapport: logga vilken bundle + SW enheten faktiskt kör (felsökning)
    (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        api.post("/push/diag", {
          logs: [{
            type: "app_boot",
            ts: Date.now(),
            bundle: (swUrl().split("v=")[1] || "dev"),
            controller: navigator.serviceWorker.controller?.scriptURL || null,
            regs: regs.map((r) => ({
              script: (r.active || r.waiting || r.installing)?.scriptURL || null,
              state: r.active ? "active" : r.waiting ? "waiting" : "installing",
            })),
            perm: typeof Notification !== "undefined" ? Notification.permission : "n/a",
            standalone: window.matchMedia?.("(display-mode: standalone)")?.matches || false,
          }],
        }).catch(() => {});
      } catch { /* tyst */ }
    })();
    const onVisible = () => {
      if (document.visibilityState === "visible") consumePendingNav("visible");
    };
    document.addEventListener("visibilitychange", onVisible);
    const onFocus = () => consumePendingNav("focus");
    window.addEventListener("focus", onFocus);

    const handler = (e) => {
      if (e.data?.type === "NAVIGATE" && typeof e.data.path === "string") {
        // Kvittera till SW:n så den vet att SPA-navigeringen lyckades
        // (annars gör den en hård navigering som fallback på iOS).
        try { e.source?.postMessage?.({ type: "NAVIGATE_ACK" }); } catch { /* tyst */ }
        try { navigator.serviceWorker.controller?.postMessage({ type: "NAVIGATE_ACK" }); } catch { /* tyst */ }
        // Rensa pending-nav så vi inte navigerar dubbelt via focus-checken
        caches.open("pending-nav-v1").then((c) => c.delete("/__pending_nav__")).catch(() => {});
        navigate(e.data.path);
        // Skicka buffrad SW-telemetri direkt (annars ligger den kvar till nästa appstart)
        setTimeout(() => flushTelemetry(null), 2000);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handler);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [navigate]);
  return null;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-container flex items-center justify-center" data-testid="loading-screen">
        <div className="text-sm text-[color:var(--text-tertiary)]">Laddar…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="app-container pb-safe pt-safe">
      <ImpersonationBanner />
      {children}
      <BottomNav />
      <PushPrompt />
    </div>
  );
};

const BareProtected = ({ children }) => {
  // Inloggning krävs, men UTAN BottomNav/Banner — för utskriftsvyer m.m.
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-screen">
        <div className="text-sm text-[color:var(--text-tertiary)]">Laddar…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <div className="print-bare bg-white min-h-screen">{children}</div>;
};

const AdminProtected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-container flex items-center justify-center" data-testid="loading-screen">
        <div className="text-sm text-[color:var(--text-tertiary)]">Laddar…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <div className="app-container pb-safe pt-safe">
      <ImpersonationBanner />
      {children}
      <BottomNav />
      <PushPrompt />
    </div>
  );
};

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        registerSW().catch(() => {});
      });
    }
  }, []);

  // Heartbeat — uppdaterar enhetens last_seen_at i backend så
  // "Aktiv · senast sedd" speglar verklig app-användning.
  // Triggar vid mount, vid fönsterfokus och var 5:e minut.
  useEffect(() => {
    let timer;
    let cancelled = false;
    const ping = async () => {
      if (cancelled || document.hidden) return;
      try {
        const { pushHeartbeat } = await import("@/lib/push");
        await pushHeartbeat();
      } catch { /* tyst */ }
    };
    ping();
    timer = setInterval(ping, 5 * 60 * 1000);
    const onFocus = () => ping();
    const onVisibility = () => { if (!document.hidden) ping(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <UpdateBanner />
        <BrowserRouter>
          <SwNavigationListener />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/google" element={<Protected><GoogleCallback /></Protected>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/kalender" element={<Protected><CalendarPage /></Protected>} />
            <Route path="/laxor" element={<Protected><HomeworkPage /></Protected>} />
            <Route path="/att-gora" element={<Protected><TodosPage /></Protected>} />
            <Route path="/koder" element={<Protected><VaultPage /></Protected>} />
            <Route path="/jarvis" element={<Protected><JarvisPage /></Protected>} />
            <Route path="/vad-ar-nytt" element={<Protected><WhatsNewPage /></Protected>} />
            <Route path="/installningar" element={<AdminProtected><SettingsPage /></AdminProtected>} />
            <Route path="/health" element={<AdminProtected><HealthPage /></AdminProtected>} />
            <Route path="/studie/:id" element={<Protected><StudyReminderPage /></Protected>} />
            <Route path="/resultat" element={<Protected><ResultsPage /></Protected>} />
            <Route path="/betyg" element={<Protected><GradesPage /></Protected>} />
            <Route path="/betyg/skriv-ut" element={<BareProtected><GradesPrintPage /></BareProtected>} />
            <Route path="/installningar/amneskatalog" element={<Protected><SubjectCatalogPage /></Protected>} />
            <Route path="/installningar/classroom-lankar" element={<AdminProtected><ClassroomLinksPage /></AdminProtected>} />
            <Route path="/meddelanden" element={<Protected><NotificationsPage /></Protected>} />
            <Route path="/nyheter/:id" element={<Protected><NewsDetailPage /></Protected>} />
            <Route path="/laddaren" element={<AdminProtected><Laddaren /></AdminProtected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InstallModal />
        </BrowserRouter>
        <Toaster
          position="top-center"
          richColors
          offset="calc(env(safe-area-inset-top, 0px) + 56px)"
          mobileOffset="calc(env(safe-area-inset-top, 0px) + 64px)"
        />
      </AuthProvider>
    </div>
  );
}

export default App;
